import React, { useCallback, useMemo, useRef } from 'react';
import { useFormContext, Controller, ControllerRenderProps, useController } from 'react-hook-form';
import {
  END_TREATMENT_LANGUAGE,
  SubscriptionEndTreatment,
} from 'services/user-patient-profile-subscriptions';
import { Grid } from '@mui/material';
import { useEffectError, useFieldProps } from 'hooks';
import { AppRadioGroup } from 'components/app-radio-group';
import { enumToArray, fieldToLabelKey } from 'utils/other';
import { FormFields, Left } from 'utils/types';
import { useTranslate } from 'hooks/use-translate';
import { AppWeightInput } from 'components/app-weight-input';
import { AppInput } from 'components/app-input';
import { apiEndOfTreatmentRecommendationTemplate } from 'services/end-of-treatment-recommendation-templates';
import { apiUserPatientPrescriptions } from 'services/user-patient-profile-prescriptions';
import { convertToDate, dateFormat } from 'utils/dates';
import { APP_FORMAT_DATE_TIME } from 'configs/const';
import { AppPrescriptionDrugsAreaInput } from 'components/app-prescription-drugs-area-input';
import { AppChipAreaInput } from 'components/app-chip-area-input';
import { AppInputTemplate } from 'components/app-input-template';

const useSourceTemplatesQuery =
  apiEndOfTreatmentRecommendationTemplate.useGetEndOfTreatmentRecommendationTemplatesSourceQuery;

const useFetchPrescriptionsQuery =
  apiUserPatientPrescriptions.useGetMedicalPrescriptionsWithDrugsQuery;

type Template = {
  title?: string | null | undefined;
};
interface Props {
  isLoading: boolean;
  userPatientProfileID: string;
}

type FormModel = Left<SubscriptionEndTreatment> & { period: string }; // `period` is a form-only field (dialog-end-treatment schema)
export const FormEndTreatment: React.FC<React.PropsWithChildren<Props>> = ({
  isLoading,
  userPatientProfileID,
}) => {
  const { t } = useTranslate();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<FormFields<FormModel>>();
  const getFieldProps = useFieldProps({ errors, disabled: isLoading });
  const languageOptions = useMemo(
    () =>
      enumToArray(END_TREATMENT_LANGUAGE).map((item) => {
        const labelKey = String(item.title).toLowerCase();
        return {
          value: item.id,
          label: t(fieldToLabelKey(labelKey)),
        };
      }),
    [t],
  );
  const messageRef = useRef<HTMLInputElement>(null);

  const sourceTemplates = useSourceTemplatesQuery();
  useEffectError(sourceTemplates.error);

  const onSelectTemplate = useCallback(
    (value: Template | null) => {
      if (!value) return;

      const message = getValues('doctorRecommendation') || '';

      setValue(
        'doctorRecommendation',
        [message.trim(), `${value.title} `].filter(Boolean).join('\n'),
      );
      messageRef.current?.focus();
    },
    [setValue, getValues],
  );

  const sourcePrescriptions = useFetchPrescriptionsQuery(userPatientProfileID);
  useEffectError(sourcePrescriptions.error);
  const prescriptionOptions = useMemo(() => {
    return (sourcePrescriptions.data || []).map((prescription) => {
      return {
        id: String(prescription.id),
        title: dateFormat(prescription.entryDate, APP_FORMAT_DATE_TIME),
        isCompleted: !!prescription.isCompleted,
        entryDate: convertToDate(prescription.entryDate),
        items: prescription.drugs.map((drug) => {
          return { id: String(drug.id), title: String(drug.drugNameEng) };
        }),
      };
    });
  }, [sourcePrescriptions.data]);
  const controlDrugs = useController({ control, name: 'prescribedMedications' });

  const renderWeight = ({ field: renderProps }: { field: ControllerRenderProps<any> }) => (
    <AppWeightInput {...getFieldProps(renderProps)} disableClearable disabled={isLoading} />
  );
  const renderBMI = ({ field: renderProps }: { field: ControllerRenderProps<any> }) => (
    <AppInput
      {...getFieldProps(renderProps)}
      disableClearable
      disabled={isLoading}
      type={'number'}
    />
  );

  return (
    <Grid container spacing={1}>
      <Grid
        size={{
          md: 6,
          lg: 5,
        }}
      >
        <AppPrescriptionDrugsAreaInput
          {...getFieldProps(controlDrugs.field)}
          label={t('prescriptions')}
          options={prescriptionOptions}
          disabled={isLoading}
        />
      </Grid>
      <Grid
        size={{
          md: 6,
          lg: 7,
        }}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
            <AppChipAreaInput {...getFieldProps(controlDrugs.field)} disabled={isLoading} />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Controller control={control} name={'startingWeight'} render={renderWeight} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Controller control={control} name={'startingBMI'} render={renderBMI} />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Controller control={control} name={'currentWeight'} render={renderWeight} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Controller control={control} name={'currentBMI'} render={renderBMI} />
          </Grid>

          <Grid size={12}>
            <Controller
              control={control}
              name={'doctorRecommendation'}
              render={({ field: renderProps }) => {
                return (
                  <AppInputTemplate
                    {...getFieldProps(renderProps)}
                    inputRef={messageRef}
                    onSelectTemplate={onSelectTemplate}
                    templates={sourceTemplates.data || []}
                    isLoading={sourceTemplates.isLoading}
                    disabled={isLoading}
                  />
                );
              }}
            />
          </Grid>

          <Grid size="grow">
            <Controller
              name={'period'}
              control={control}
              render={({ field: renderProps }) => <AppInput {...getFieldProps(renderProps)} />}
            />
          </Grid>
          <Grid size="auto">
            <Controller
              name={'language'}
              control={control}
              render={({ field: renderProps }) => (
                <AppRadioGroup row {...getFieldProps(renderProps)} options={languageOptions} />
              )}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
